/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Fragment } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import ValueSliderGroup, { SliderInterface } from './ValueSliderGroup';
import { validateMinMax } from '../../../../utils/universalTrackerValue';
import { renderInputGroup } from './InputGroup';
import { InputGroupPrePend } from './InputGroupPrePend'
import { BaseInputProps } from './InputProps';
import { ValueDataData, ValueDataObject } from '../../question/questionInterfaces';
import { InputHelperText } from './InputHelperText';

interface handleNumericValueListChangeProps extends Pick<BaseInputProps, 'valueDataData' | 'handleValueDataChange' | 'handleError'> {
  e: React.ChangeEvent<HTMLInputElement>,
  min?: number,
  max?: number
}

const handleNumericValueListChange = ({ e, min, max, valueDataData, handleValueDataChange, handleError }: handleNumericValueListChangeProps) => {
  e.preventDefault();
  const value = e.target.value;
  const name = e.target.name;

  const { errored, message } = validateMinMax(value, min, max);
  if (errored) {
    return handleError(message);
  }

  if (typeof valueDataData !== 'object' || Array.isArray(valueDataData)) {
    valueDataData = {};
  }
  valueDataData[name] = value === '' ? undefined : value;
  handleValueDataChange(valueDataData);
}
interface numericValueListItemProps extends BaseInputProps {
  label?: string,
  inputName: string,
  value?: number | string,
  isTotal: boolean
}
const renderNumericValueListItem = (props: numericValueListItemProps) => {
  const {
    universalTracker,
    inputName,
    value,
    isTotal = false,
    handleFocus,
    index,
    displayCheckbox,
    handleCheckboxChange,
    handleValueDataChange,
    handleUnitChange,
    valueDataData,
    handleError,
    isDisabled,
    placeholder,
    inputMessage,
  } = props;

  const min = universalTracker.getMin();
  const max = universalTracker.getMax();
  const checked = isTotal || (inputName !== '' && Boolean(displayCheckbox[inputName]));
  const checkboxId = `valueCheckbox-${inputName}-${index}-${universalTracker.getId()}`;
  const label = props.label + (checked ? '' : ': N/A');
  const isValidValue = value !== undefined && value !== '' && !isNaN(value as number);
  const disabled = Boolean(isDisabled?.(props));
  const invalidMessage = inputMessage?.[inputName];
  const isInvalid = !!invalidMessage;

  return <Fragment key={checkboxId}>
    <div className='inputGroupContainer my-2'>
      <FormGroup check>
        {!isTotal ? <Input
          type='checkbox'
          id={checkboxId}
          checked={checked}
          disabled={disabled}
          onChange={(e) => handleCheckboxChange(inputName, e.target.checked)} />
        : null}
        <Label check htmlFor={checkboxId} className='strong'>{label}</Label>
      </FormGroup>
    </div>
    <div className={!checked ? 'd-none mb-3' : ' mb-3'}>
      <div className='input-group has-validation mb-3'>
        {InputGroupPrePend({ ...props, isInvalid })}
        {isTotal ? <Input
          type='text'
          className='styled-readonly'
          name={inputName}
          value={isValidValue ? value : ''}
          aria-describedby='suffix'
          disabled={isTotal}
        />
          :
          <Input
            type='number'
            placeholder={placeholder}
            autoComplete='off'
            className={isInvalid ? '' : 'styled-input'}
            name={inputName}
            value={isValidValue ? value : ''}
            aria-describedby='suffix'
            disabled={disabled}
            required={true}
            onFocus={handleFocus}
            onClick={handleFocus}
            invalid={isInvalid}
            onWheel={e => e.currentTarget.blur()}
            onChange={(e) => handleNumericValueListChange({ e, min, max, valueDataData, handleValueDataChange, handleError })} />
        }
        {renderInputGroup({
          ...props,
          isInvalid,
          handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
            handleUnitChange(e.target.value), isDisabled: (props) => !isTotal || disabled
        })}
         <InputHelperText invalidMessage={invalidMessage} universalTracker={universalTracker} />
      </div>
    </div>
  </Fragment>
};
interface sliderChangeProps extends Pick<BaseInputProps, 'handleValueDataChange' | 'handleCheckboxChange'> {
  data: SliderInterface[]
}
const handleSliderChange = ({ data, handleValueDataChange, handleCheckboxChange }: sliderChangeProps) => {
  const valueData: ValueDataObject = {};
  data.forEach(d => {
    valueData[d.code] = d.value;
    handleCheckboxChange(d.code, true); // No checkboxes, so auto-enable
  });
  handleValueDataChange(valueData);
}
interface NumericValueListProps extends BaseInputProps {
}
export default function NumericValueList(props: NumericValueListProps) {
  const {
    universalTracker,
    valueDataData,
    questionValue,
    handleCheckboxChange,
    handleValueDataChange
  } = props;

  const valueList = universalTracker.getValueListOptions();
  const defaultValue = typeof valueDataData !== 'object' ? {} as ValueDataData : valueDataData;

  if (universalTracker.hasMinAndMax()) {
    let sliders: SliderInterface[] = [];
    valueList.forEach((item) => {
      const value = defaultValue[item.code as keyof typeof valueDataData]
      const slider = {
        code: item.code,
        name: item.name,
        value: parseFloat(typeof value === 'string' ? value : ''),
      };
      sliders.push(slider);
    });

    if (sliders === undefined || sliders.length === 0) {
      return <></>;
    }
    return <ValueSliderGroup sliders={sliders} universalTracker={universalTracker} handleChange={(data) => handleSliderChange({ data, handleValueDataChange, handleCheckboxChange })} />
  }

  return <div className='questionWithFields'>
    {valueList.map((item) => {
      const value = defaultValue[item.code as keyof typeof valueDataData]
      return renderNumericValueListItem({
        ...props,
        label: item.name,
        inputName: item.code,
        value: value,
        isTotal: false
      })
    })}
    <div className='display-on-focus'>
      {renderNumericValueListItem({
        ...props,
        label: 'Total',
        value: questionValue,
        isTotal: true,
        inputName: ''
      })}
    </div>
  </div>;
}
